
import FormSelect from "./FormSelect";
import React, { Fragment, useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import { FaCheck } from "react-icons/fa6";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppIcons } from "../../assets/icon/appIcons";
import useModal from "../../hooks/useModal";
import {
  getSearch,
  isArray,
  kindOf,
  saveSearch,
  showToast,
} from "../../lib/utility";
import { middlewareCreate } from "../../redux/store";
import { saveFilter } from "../Report/ReportSlice";
import FormEnter from "./FormEnter";
import SaveFilter from "./SaveFilter";

const TABLE_SELECTOR = ".rdt_Table, .customTableComponent, .ant-table, table";
const ROW_SELECTOR = ".rdt_TableRow, .tableRowContainer, .ant-table-row";
const EMPTY_STATE_SELECTOR = ".dashboard-graph-empty-state";
const LOADING_SELECTOR = ".skeleton-table, .react-loading-skeleton";

const hasTableRows = (container) => {
  if (container.querySelector(ROW_SELECTOR)) return true;
  return Array.from(container.querySelectorAll("tbody tr")).some(
    (tr) => !tr.querySelector(EMPTY_STATE_SELECTOR)
  );
};

const isTableEmpty = (container) =>
  !hasTableRows(container) &&
  Boolean(container.querySelector(`${TABLE_SELECTOR}, ${EMPTY_STATE_SELECTOR}`));

const TableWithHeader = ({
  name,
  setShowFilterModal,
  search,
  serverSearch = false,
  searchOnBlur = true,
  customChildren,
  charts,
  customChildrenPlacement = "first",
  showSavedFilter,
  handleSavedFilterClick,
  children,
  isPinned = false,
  className = "",
  style = {},
  tableMinHeight,
  grow = true,
}) => {
  const { t } = useTranslation(["common", "social"]);
  let showSearch = !!search;
  search = search ?? {};
  let {
    searchVal,
    setSearchVal,
    handleSearch,
    saveSearchKey,
    saveFilterKey,
    filterData,
    refreshFilter,
    disabled: searchDisabledProp,
  } = search;

  const [filterMobActive, setFilterMobActive] = useState(false);
  const [selectedSavedFilter, setSelectedSavedFilter] = useState("");
  const [savedSearch, setSavedSearch] = useState(false);
  const [tableEmpty, setTableEmpty] = useState(false);

  const inputRef = useRef();
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!showSearch || searchDisabledProp !== undefined || !container) return;

    let frame = 0;
    const evaluate = () => {
      frame = 0;
      if (container.querySelector(LOADING_SELECTOR)) return;
      setTableEmpty(isTableEmpty(container));
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(evaluate);
    };

    evaluate();
    const observer = new MutationObserver(schedule);
    observer.observe(container, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [showSearch, searchDisabledProp]);

  // Never lock the input while it holds a query, or a zero-result search could not be cleared.
  const searchDisabled =
    (searchDisabledProp ?? tableEmpty) &&
    !searchVal &&
    !inputRef.current?.value &&
    document.activeElement !== inputRef.current;
  const dispatch = useDispatch();
  const { clickRef, contRef, showModal, handleOpen, handleClose } = useModal();
  const { x, y, right, height, width } =
    clickRef.current?.getBoundingClientRect() ?? {};

  const handleFilterActive = (e) => {
    if (!filterMobActive) {
      e?.preventDefault();
      inputRef.current.focus();
      setFilterMobActive(true);
    }

    if (searchVal && searchVal !== "" && kindOf(handleSearch, "function")) {
      handleSearch(e);
    }
  };

  const handleBlur = (e) => {
    setFilterMobActive(false);
    if (serverSearch && searchOnBlur) handleSearch(e);
  };

  const handleSaveSearch = () => {
    if (!searchVal || searchVal === "") {
      showToast(
        t("commonUi.searchValueEmpty", {
          ns: "common",
          defaultValue: "Search Value is empty",
        }),
        "warning"
      );
      return;
    }
    saveSearch(saveSearchKey, searchVal);
    setSavedSearch(true);
    setTimeout(() => {
      setSavedSearch(false);
    }, 2000);
  };

  const handleSaveClick = (saveText) => {
    if (saveText === "") {
      showToast(
        t("commonUi.filterNameEmpty", {
          ns: "common",
          defaultValue: "Filter name can not be empty",
        }),
        "warning"
      );
      return;
    }

    dispatch(
      saveFilter({
        recentFilterName: saveText,
        purpose: saveFilterKey,
        createdOn: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        specialAttributes: { ...filterData, searchValue: searchVal },
      })
    ).then((action) =>
      middlewareCreate(
        action,
        "Save Filter",
        () => {
          refreshFilter && refreshFilter();
          handleClose();
          setSavedSearch(true);
          setTimeout(() => {
            setSavedSearch(false);
          }, 2000);
        },
        undefined,
        {
          modal: handleClose,
        }
      )
    );
  };

  const handleSavedFilterChange = (e) => {
    let id = e.target.value;
    let specialAttributes = showSavedFilter.find(
      (sf) => sf.id === id
    )?.specialAttributes;

    setSelectedSavedFilter(id);
    specialAttributes &&
      handleSavedFilterClick &&
      handleSavedFilterClick(specialAttributes);
  };

  return (
    <>
      {charts && (
        <div className="charts | row mb-4 g-3">
          {charts.map((chart, index) => (
            <div className="col-12 col-md-6" key={index}>
              {chart}
            </div>
          ))}
        </div>
      )}
      <div
        className={
          "commonTableHeader d-flex" +
          (grow ? " flex-grow-1" : "") +
          (className ? " " + className : "")
        }
        style={{ flexDirection: "column", minHeight: tableMinHeight, ...style }}
        ref={containerRef}
      >
        <div
          className={
            "d-flex align-items-center flex-wrap commonTable" +
            (name ? " justify-content-between" : " noName justify-content-end")
          }
        >
          {name && (
            <div className="d-flex align-items-center" style={{ gap: 6 }}>
              {typeof name === "string" ? (
                <p className="name mb-0">{name}</p>
              ) : (
                <div className="name mb-0 d-inline-flex align-items-center">{name}</div>
              )}
              {isPinned && (
                <span className="badge bg-warning text-dark text-uppercase fw-600">
                  {t("commonUi.pinned", { ns: "common", defaultValue: "Pinned" })}
                </span>
              )}
            </div>
          )}
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            {customChildrenPlacement === "first" && (
              <div
                className="custom | d-flex align-items-center"
                style={{
                  gap: 8,
                  flexShrink: 0,
                  minWidth: "max-content",
                }}
              >
                {isArray(customChildren)
                  ? customChildren.map((child, index) => (
                      <Fragment key={index}>{child}</Fragment>
                    ))
                  : customChildren}
              </div>
            )}
            {setShowFilterModal && (
              <button
                className="filterBtn"
                onClick={() => setShowFilterModal(true)}
              >
                <img
                  src={AppIcons.filter}
                  alt=""
                  style={{ width: 16, height: 16 }}
                  loading="lazy"
                />
                <span>
                  {t("commonUi.filter", { ns: "common", defaultValue: "Filter" })}
                </span>
              </button>
            )}
            {saveFilterKey && (
              <div
                className="dropdown savedFilter"
                style={{ maxWidth: 200, minWidth: 0 }}
              >
                <FormSelect
                  className="saved-filter-trigger"
                  value={selectedSavedFilter}
                  onChange={handleSavedFilterChange}
                  onFocus={() => refreshFilter && refreshFilter()}
                  style={{ borderRadius: 8, fontSize: "14px" }}
                  ellipsisTooltip
                >
                  <option value="">
                    {t("commonUi.savedFilters", {
                      ns: "common",
                      defaultValue: "-Saved Filters-",
                    })}
                  </option>
                  {(Array.isArray(showSavedFilter) ? showSavedFilter : []).map(
                    (f, index) => (
                      <option key={index} value={f.id}>
                        {f.recentFilterName}
                      </option>
                    )
                  )}
                </FormSelect>
              </div>
            )}
            {showSearch && (
              <>
                <FormEnter
                  {...(serverSearch ? { onSubmit: handleSearch } : {})}
                >
                  <div
                    className={
                      "searchInput" +
                      (filterMobActive || searchVal !== "" ? " active" : "")
                    }
                    style={
                      searchDisabled
                        ? { opacity: 0.6, cursor: "not-allowed" }
                        : undefined
                    }
                  >
                    <input
                      type="text"
                      disabled={searchDisabled}
                      style={searchDisabled ? { cursor: "not-allowed" } : undefined}
                      placeholder={t("commonUi.search", {
                        ns: "common",
                        defaultValue: "Search...",
                      })}
                      value={searchVal}
                      onChange={(e) =>
                        setSearchVal
                          ? setSearchVal(e.target.value)
                          : handleSearch(e)
                      }
                      onBlur={handleBlur}
                      list={saveSearchKey}
                      ref={inputRef}
                    />
                    {saveSearchKey && (
                      <datalist id={saveSearchKey}>
                        {getSearch(saveSearchKey).map((value) => {
                          return <option value={value} key={value}></option>;
                        })}
                      </datalist>
                    )}
                    <button
                      type="submit"
                      onClick={handleFilterActive}
                      disabled={searchDisabled}
                      style={searchDisabled ? { cursor: "not-allowed" } : undefined}
                    >
                      <CiSearch
                        className={"fs-25" + (name ? " text-white" : "")}
                      />
                    </button>
                  </div>
                </FormEnter>
                {saveSearchKey && (
                  <div
                    className="saveSearch cursor-pointer"
                    onClick={handleSaveSearch}
                  >
                    {!savedSearch && (
                      <img
                        src={AppIcons.save}
                        alt=""
                        className="fs-20"
                        style={{ width: 20, height: 20 }}
                        loading="lazy"
                      />
                    )}
                    {savedSearch && <FaCheck className="text-blue fs-20" />}
                    {!savedSearch && (
                      <span>
                        {t("commonUi.saveSearch", {
                          ns: "common",
                          defaultValue: "Save Search",
                        })}
                      </span>
                    )}
                    {savedSearch && (
                      <span>
                        {t("commonUi.savedSearch", {
                          ns: "common",
                          defaultValue: "Saved Search",
                        })}
                      </span>
                    )}
                  </div>
                )}
                {saveFilterKey && (
                  <div
                    className="saveSearch cursor-pointer"
                    onClick={handleOpen}
                    ref={clickRef}
                  >
                    {!savedSearch && (
                      <img
                        src={AppIcons.save}
                        alt=""
                        className="fs-20"
                        style={{ width: 20, height: 20 }}
                        loading="lazy"
                      />
                    )}
                    {savedSearch && <FaCheck className="text-blue fs-20" />}
                    {!savedSearch && (
                      <span>
                        {t("commonUi.saveFilter", {
                          ns: "common",
                          defaultValue: "Save Filter",
                        })}
                      </span>
                    )}
                    {savedSearch && (
                      <span>
                        {t("commonUi.savedFilter", {
                          ns: "common",
                          defaultValue: "Saved Filter",
                        })}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
            {customChildrenPlacement === "last" && (
              <div
                className="custom | d-flex align-items-center"
                style={{
                  gap: 8,
                  flexShrink: 0,
                  minWidth: "max-content",
                }}
              >
                {customChildren}
              </div>
            )}
          </div>
        </div>
        {showModal && (
          <SaveFilter
            handleSaveClick={handleSaveClick}
            handleCancelClick={handleClose}
          />
        )}
        {children}
      </div>
    </>
  );
};

export default TableWithHeader;
